import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import LastLiveReplaySection from "../../pages/CreatorPages/LiveReplaysPage/components/LastLiveReplaySection";
import LiveReplayListSection from "../../pages/CreatorPages/LiveReplaysPage/components/LiveReplayListSection";
import { liveService } from "../../services";
import "./index.less";

interface RebroadcastItem {
  id: string;
  thumbnail: string;
  liveDescription: string;
  date: string;
  time: string;
  creatorName?: string;
  tags?: string[];
  isSubscribed?: boolean;
  creatorId?: string;
}

interface ApiRebroadcastItem {
  _id: string;
  creator: {
    _id?: string;
    avatarPath?: string;
    avatar?: string;
    name?: string;
    username?: string;
    contentType?: string[];
  };
  scheduleData?: {
    message?: string;
    media?: {
      absolutePath?: string;
    };
  };
  createdAt?: string;
  lastStreamingTime?: string;
  isSubscribed?: boolean;
}

interface ApiRebroadcastsResponse {
  week: ApiRebroadcastItem[];
  month: ApiRebroadcastItem[];
}

interface RebroadcastsResponse {
  lastLive?: RebroadcastItem;
  weeklyBroadcasts?: RebroadcastItem[];
  last30DaysBroadcasts?: RebroadcastItem[];
}

const FanLiveReplaysComponent = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [rebroadcasts, setRebroadcasts] = useState<RebroadcastsResponse>({
    lastLive: undefined,
    weeklyBroadcasts: [],
    last30DaysBroadcasts: [],
  });
  const hasFetched = useRef(false);

  const transformApiData = (apiData: ApiRebroadcastsResponse): RebroadcastsResponse => {
    const formatDateTime = (dateString?: string) => {
      if (!dateString) return { date: "", time: "" };
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }),
        time: date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
      };
    };

    const transformItem = (item: ApiRebroadcastItem): RebroadcastItem => ({
      id: item._id,
      thumbnail: item.scheduleData?.media?.absolutePath || item.creator.avatar || item.creator.avatarPath || "",
      creatorName: item.creator.name || item.creator.username || "",
      liveDescription: item.scheduleData?.message || "",
      tags: item.creator.contentType || [],
      isSubscribed: item.isSubscribed,
      creatorId: item.creator._id,
      ...formatDateTime(item.lastStreamingTime || item.createdAt),
    });

    const weekItems = apiData.week || [];
    const monthItems = apiData.month || [];

    return {
      lastLive: weekItems.length > 0 ? transformItem(weekItems[0]) : undefined,
      weeklyBroadcasts: weekItems.map(transformItem),
      last30DaysBroadcasts: monthItems.map(transformItem),
    };
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchRebroadcasts = async () => {
      try {
        setLoading(true);
        const apiData = (await liveService.getRebroadcasts()) as ApiRebroadcastsResponse;
        const transformedData = transformApiData(apiData);
        setRebroadcasts(transformedData);
      } catch (err: any) {
        setError(err || "Failed to load rebroadcasts");
      } finally {
        setLoading(false);
      }
    };

    fetchRebroadcasts();
  }, []);

  const { lastLive, weeklyBroadcasts, last30DaysBroadcasts } = rebroadcasts;

  if (loading) {
    return (
      <div className="fan-live-replays-component">
        <div className="skeleton-last-live">
          <div className="skeleton-box skeleton-large" />
        </div>
        <div className="skeleton-list-section">
          <div className="skeleton-box skeleton-title" />
          <div className="skeleton-replay-list">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton-box skeleton-item" />
            ))}
          </div>
        </div>
        <div className="skeleton-list-section">
          <div className="skeleton-box skeleton-title" />
          <div className="skeleton-replay-list">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton-box skeleton-item" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="fan-live-replays-component">{error}</div>;
  }

  return (
    <div className="fan-live-replays-component">
      {lastLive && (
        <LastLiveReplaySection
          thumbnail={lastLive.thumbnail}
          creatorName={lastLive.creatorName || ""}
          liveDescription={lastLive.liveDescription}
          tags={lastLive.tags || []}
          replayId={lastLive.id}
          showSubscribeButton={true}
          isSubscribed={lastLive.isSubscribed}
          creatorId={lastLive.creatorId}
        />
      )}
      {weeklyBroadcasts && weeklyBroadcasts.length > 0 && <LiveReplayListSection title="Diffusion de la semaine" items={weeklyBroadcasts} />}
      {last30DaysBroadcasts && last30DaysBroadcasts.length > 0 && (
        <LiveReplayListSection title="Diffusion des 30 derniers jours" items={last30DaysBroadcasts} />
      )}
      {!lastLive && (!weeklyBroadcasts || weeklyBroadcasts.length === 0) && (!last30DaysBroadcasts || last30DaysBroadcasts.length === 0) && (
        <div className="no-rebroadcasts">{t("no-rebroadcasts-message")}</div>
      )}
    </div>
  );
};

export default FanLiveReplaysComponent;
